import React, { useState, useEffect } from 'react';
import { FlaskConical, BarChart3, MessageCircle, Shield } from 'lucide-react';

interface BetaDisclaimerModalProps {
  completedCount: number;
  onClose: () => void;
}

export const BetaDisclaimerModal: React.FC<BetaDisclaimerModalProps> = ({ completedCount, onClose }) => {
  const [accepted, setAccepted] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const seen = localStorage.getItem('vidly_beta_seen');
    if (!seen) {
      setVisible(true);
    }
  }, []);

  if (!visible) return null;

  const handleStart = () => {
    localStorage.setItem('vidly_beta_seen', 'true');
    onClose();
    setVisible(false);
  };

  const progress = Math.min(100, Math.round((completedCount / 100) * 100));

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <div className="w-full max-w-[480px] max-h-[90vh] overflow-y-auto bg-[#0c0c0e] border border-zinc-800 rounded-2xl shadow-2xl overflow-hidden">

        {/* Header */}
        <div className="p-6 pb-4 text-center border-b border-zinc-800/60">
          <span className="text-3xl">🚀</span>
          <h2 className="text-lg font-semibold text-white">
            Bienvenido a la Beta de Vidly AI
          </h2>
          <p className="text-xs text-zinc-500 mt-1">
            Lee esto antes de empezar — solo toma 30 segundos
          </p>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4">

          {/* 1. Beta Phase */}
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-zinc-800/60 border border-zinc-700/60 flex items-center justify-center flex-shrink-0 mt-0.5 text-zinc-300">
              <FlaskConical className="w-4 h-4" />
            </div>
            <div>
              <h3 className="text-xs font-semibold text-white">Fase Beta</h3>
              <p className="text-[11px] text-zinc-400 leading-relaxed mt-0.5">
                Estamos en fase de pruebas. La IA puede cometer errores: cortar frases a mitad, seleccionar momentos poco relevantes o estimar duraciones inexactas. Cada error nos ayuda a mejorar.
              </p>
            </div>
          </div>

          {/* 2. Limited to 100 videos */}
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-zinc-800/60 border border-zinc-700/60 flex items-center justify-center flex-shrink-0 mt-0.5 text-zinc-300">
              <BarChart3 className="w-4 h-4" />
            </div>
            <div className="flex-1">
              <h3 className="text-xs font-semibold text-white">Limitada a 100 vídeos</h3>
              <p className="text-[11px] text-zinc-400 leading-relaxed mt-0.5">
                Esta beta está limitada a 100 vídeos totales. Cada vídeo procesado entrena y mejora nuestra IA.
              </p>
              {/* Progress bar */}
              <div className="mt-2">
                <div className="flex items-center justify-between text-[10px] text-zinc-500 mb-1">
                  <span>{completedCount} procesados</span>
                  <span>{100 - completedCount} restantes</span>
                </div>
                <div className="w-full h-1.5 rounded-full bg-zinc-800 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-emerald-500 transition-all duration-500"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* 3. Feedback */}
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-zinc-800/60 border border-zinc-700/60 flex items-center justify-center flex-shrink-0 mt-0.5 text-zinc-300">
              <MessageCircle className="w-4 h-4" />
            </div>
            <div>
              <h3 className="text-xs font-semibold text-white">Tu feedback es oro</h3>
              <p className="text-[11px] text-zinc-400 leading-relaxed mt-0.5">
                Después de cada resumen, podrás valorar el resultado. Tu opinión directa es lo que moldea la calidad final.
              </p>
            </div>
          </div>

          {/* 4. Privacy */}
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-zinc-800/60 border border-zinc-700/60 flex items-center justify-center flex-shrink-0 mt-0.5 text-zinc-300">
              <Shield className="w-4 h-4" />
            </div>
            <div>
              <h3 className="text-xs font-semibold text-white">Privacidad</h3>
              <p className="text-[11px] text-zinc-400 leading-relaxed mt-0.5">
                Tus vídeos se eliminan automáticamente en 24h. No se usan para entrenar modelos externos.
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 pt-4 border-t border-zinc-800/60 space-y-3">
          <label className="flex items-start gap-2.5 cursor-pointer group">
            <input
              type="checkbox"
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
              className="mt-0.5 w-4 h-4 rounded border-zinc-700 bg-zinc-900 text-emerald-500 focus:ring-emerald-500 focus:ring-offset-0 cursor-pointer"
            />
            <span className="text-[11px] text-zinc-400 leading-relaxed group-hover:text-zinc-300 transition-colors">
              Entiendo que es una beta y los resultados pueden ser imperfectos
            </span>
          </label>

          <button
            onClick={handleStart}
            disabled={!accepted}
            className="w-full py-2.5 rounded-xl text-sm font-medium transition-all disabled:opacity-30 disabled:cursor-not-allowed bg-white text-zinc-950 hover:bg-zinc-200 active:scale-[0.98]"
          >
            Empezar
          </button>
        </div>

      </div>
    </div>
  );
};
